// sRGB → OKLCH conversion and OKLCH-space adjustments. Complements lib/color
// (which parses colour strings and converts oklch() → sRGB): this module goes
// the other way so any accepted colour (hex, rgb(), hsl(), oklch()) can be
// normalised to a canonical oklch() string for storage, and so derived colours
// (hover shades, readable text) can be computed perceptually.

import { parseColor, colorFamily, type RgbaColor } from "./color";

export interface OklchColor {
  /** Lightness 0–1 */
  l: number;
  /** Chroma ≥ 0 (~0–0.4 in sRGB gamut) */
  c: number;
  /** Hue in degrees 0–360 */
  h: number;
  /** Alpha 0–1 */
  a: number;
}

function gammaToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Linear sRGB → OKLab (matrices from the OKLab reference implementation). */
function linearSrgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** An sRGB colour as OKLCH components. */
export function rgbaToOklch({ r, g, b, a }: RgbaColor): OklchColor {
  const [L, labA, labB] = linearSrgbToOklab(gammaToLinear(r), gammaToLinear(g), gammaToLinear(b));
  const c = Math.sqrt(labA * labA + labB * labB);
  // Hue is meaningless at zero chroma; pin it so greys are stable.
  const h = c < 1e-6 ? 0 : ((Math.atan2(labB, labA) * 180) / Math.PI + 360) % 360;
  return { l: Math.min(1, Math.max(0, L)), c, h, a };
}

const round = (value: number, dp: number) => {
  const f = 10 ** dp;
  return Math.round(value * f) / f;
};

/** Canonical oklch() string, e.g. "oklch(0.546 0.245 262.9)" (+ " / 50%"). */
export function formatOklch({ l, c, h, a }: OklchColor): string {
  const body = `oklch(${round(l, 3)} ${round(c, 3)} ${round(h, 1)})`;
  if (a >= 1) return body;
  return body.replace(")", ` / ${Math.round(Math.min(1, Math.max(0, a)) * 100)}%)`);
}

/** Parse any supported colour string to OKLCH components; null if invalid. */
export function oklchComponents(value: string): OklchColor | null {
  const parsed = parseColor(value);
  return parsed ? rgbaToOklch(parsed) : null;
}

/**
 * Normalise any accepted colour string to a canonical oklch() string. Values
 * already in oklch() are kept verbatim (no round-trip quantisation); values
 * that can't be parsed are returned unchanged.
 */
export function toOklch(value: string): string {
  const trimmed = value.trim();
  if (colorFamily(trimmed) === "oklch") return trimmed;
  const parsed = parseColor(trimmed);
  return parsed ? formatOklch(rgbaToOklch(parsed)) : value;
}

/** The same colour with its OKLCH lightness replaced (clamped to 0–1). */
export function withOklchLightness(color: OklchColor, l: number): OklchColor {
  return { ...color, l: Math.min(1, Math.max(0, l)) };
}
